import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { userAPI, authAPI } from "./services/apiClient";
import { useFileStore } from "./stores/fileStore";
import { User, UserDetail, SecurityUserDetail } from "./types";

// Query Keys
export const queryKeys = {
  files: ["files"] as const,
  file: (username: string) => ["file", username] as const,
  currentFile: ["currentFile"] as const,
  fileDetail: (username: string) => ["fileDetail", username] as const,
  fileSecureInfo: ["fileSecureInfo"] as const,
};

// 文件相关查询hooks
export const useFileQuery = () => {
  const setCurrentFile = useFileStore((state) => state.setCurrentFile);

  return useQuery<User | null>({
    queryKey: queryKeys.currentFile,
    queryFn: async () => {
      try {
        console.log("useFileQuery - 开始获取当前文件信息...");
        const response = await userAPI.getCurrentUser();
        console.log("useFileQuery - API原始响应:", response);

        // 检查API响应结构：可能是直接返回数据对象，也可能是ApiResponse结构
        if (isFileResponse(response)) {
          // API直接返回了文件数据对象
          console.log("useFileQuery - API直接返回文件数据:", response);
          const file = convertToFile(response);
          console.log("useFileQuery - 转换后的文件数据:", file);
          setCurrentFile(file);
          return file;
        } else if (
          response &&
          typeof response === "object" &&
          "data" in response
        ) {
          // API返回了ApiResponse结构
          if (response.data && isFileResponse(response.data)) {
            const file = convertToFile(response.data);
            console.log("useFileQuery - 转换后的文件数据:", file);
            setCurrentFile(file);

            if (response.success) {
              console.log("useFileQuery - API响应成功:", response.data);
            } else {
              console.warn(
                "useFileQuery - API返回了有效数据但success标志为false，仍然使用数据:",
                response.data
              );
            }
            return file;
          } else {
            // 没有有效数据时，根据success标志判断
            if (response.success) {
              console.error(
                "useFileQuery - API响应success=true但没有有效数据:",
                response
              );
            } else {
              console.error("useFileQuery - API响应失败:", response);
            }
            return null;
          }
        } else {
          // 未知的响应结构，尝试直接解析
          console.warn(
            "useFileQuery - API返回了未知的响应结构，尝试直接解析:",
            response
          );

          // 尝试检查是否有文件数据字段
          if (
            response &&
            typeof response === "object" &&
            ("username" in response || "username" in response)
          ) {
            console.log(
              "useFileQuery - 检测到文件数据字段，尝试转换:",
              response
            );
            // 使用类型断言转换为FileResponse，确保包含必要的字段
            const fileData = response as unknown as FileResponse;
            const file = convertToFile(fileData);
            console.log("useFileQuery - 直接解析后的文件数据:", file);
            setCurrentFile(file);
            return file;
          }

          return null;
        }
      } catch (error) {
        console.error("useFileQuery - API请求失败:", error);
        return null;
      }
    },
    staleTime: 5 * 60 * 1000, // 5分钟
    gcTime: 10 * 60 * 1000, // 10分钟
    retry: 2,
  });
};

export const useFilesQuery = () => {
  const setFiles = useFileStore((state) => state.setFiles);

  return useQuery<User[] | undefined>({
    queryKey: queryKeys.files,
    queryFn: async () => {
      const response = await userAPI.getUsers();
      if (response.success && response.data) {
        setFiles(response.data as User[]);
      }
      return response.data as User[] | undefined;
    },
    staleTime: 2 * 60 * 1000, // 2分钟
    gcTime: 5 * 60 * 1000, // 5分钟
  });
};

export const useFileDetailQuery = () => {
  const setFileDetail = useFileStore((state) => state.setFileDetail);

  return useQuery<UserDetail | null>({
    queryKey: queryKeys.fileDetail("current"),
    queryFn: async () => {
      try {
        const response = await userAPI.getUserDetail();

        // 检查API响应结构：可能是直接返回数据对象，也可能是ApiResponse结构
        if (response && typeof response === "object" && "_id" in response) {
          // API直接返回了文件详情数据对象
          console.log(
            "useFileDetailQuery - API直接返回文件详情数据:",
            response
          );
          // 从响应数据中获取username（使用username作为文件标识）
          const username = String(
            (response as any).username || (response as any).username
          );
          if (username) {
            // 确保数据格式与UserDetail类型兼容，包含所有实际返回的字段
            const fileDetailData: UserDetail = {
              username:
                (response as any).username || (response as any).username, // 后端返回username字段，兼容userName
              nickName:
                (response as any).nickname || (response as any).nickName, // 后端返回nickname字段，兼容nickName
              source: (response as any).source,
              avatarPath: (response as any).avatarPath,
              createdAt: (response as any).createdAt,
              updatedAt: (response as any).updatedAt,
              _class: (response as any)._class,
              phone: (response as any).phone,
              address: (response as any).address,
              department: (response as any).department,
              position: (response as any).position,
            };
            setFileDetail(username, fileDetailData);
          }
          // 确保返回的数据格式与UserDetail类型兼容，包含所有实际返回的字段
          return {
            username: (response as any).username || (response as any).username, // 后端返回username字段，兼容userName
            nickName: (response as any).nickname || (response as any).nickName, // 后端返回nickname字段，兼容nickName
            source: (response as any).source,
            avatarPath: (response as any).avatarPath,
            createdAt: (response as any).createdAt,
            updatedAt: (response as any).updatedAt,
            _class: (response as any)._class,
            phone: (response as any).phone,
            address: (response as any).address,
            department: (response as any).department,
            position: (response as any).position,
          } as UserDetail;
        } else if (
          response &&
          typeof response === "object" &&
          "data" in response
        ) {
          // API返回了ApiResponse结构
          if (
            response.data &&
            typeof response.data === "object" &&
            "_id" in response.data
          ) {
            // 从响应数据中获取username（使用username作为文件标识）
            const username =
              (response.data as any).username ||
              (response.data as any).username;
            if (username) {
              // 确保数据格式与UserDetail类型兼容，包含所有实际返回的字段
              const fileDetailData: UserDetail = {
                username:
                  (response.data as any).username ||
                  (response.data as any).username, // 后端返回username字段，兼容userName
                nickName:
                  (response.data as any).nickname || response.data.nickName, // 后端返回nickname字段，兼容nickName
                source: response.data.source,
                avatarPath: response.data.avatarPath,
                createdAt: response.data.createdAt,
                updatedAt: response.data.updatedAt,
                _class: response.data._class,
                phone: response.data.phone,
                address: response.data.address,
                department: response.data.department,
                position: response.data.position,
              };
              setFileDetail(username, fileDetailData);
            }

            if (response.success) {
              console.log("useFileDetailQuery - API响应成功:", response.data);
            } else {
              console.warn(
                "useFileDetailQuery - API返回了有效数据但success标志为false，仍然使用数据:",
                response.data
              );
            }
            // 确保返回的数据格式与UserDetail类型兼容，包含所有实际返回的字段
            return {
              username:
                (response.data as any).username ||
                (response.data as any).username, // 后端返回username字段，兼容userName
              nickName:
                (response.data as any).nickname || response.data.nickName, // 后端返回nickname字段，兼容nickName
              source: response.data.source,
              avatarPath: response.data.avatarPath,
              createdAt: response.data.createdAt,
              updatedAt: response.data.updatedAt,
              _class: response.data._class,
              phone: response.data.phone,
              address: response.data.address,
              department: response.data.department,
              position: response.data.position,
            } as UserDetail;
          } else {
            // 没有有效数据时，根据success标志判断
            if (response.success) {
              console.error(
                "useFileDetailQuery - API响应success=true但没有有效数据:",
                response
              );
            } else {
              console.error("useFileDetailQuery - API响应失败:", response);
            }
            return null;
          }
        } else {
          // 未知的响应结构
          console.error(
            "useFileDetailQuery - API返回了未知的响应结构:",
            response
          );
          return null;
        }
      } catch (error) {
        console.error("useFileDetailQuery - API请求失败:", error);
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
  });
};

// 后端返回的文件安全信息数据结构
interface SecureFileDetailResponse {
  id: number; // 后端返回的是Long类型
  username: string; // 后端实际返回username字段（小写）
  email: string;
  firstName?: string;
  lastName?: string;
  enabled?: boolean;
  accountNonExpired?: boolean;
  credentialsNonExpired?: boolean;
  accountNonLocked?: boolean;
  createdAt?: string; // LocalDateTime转换为字符串
  updatedAt?: string; // LocalDateTime转换为字符串
  roles?: string;
}

// 后端返回的当前文件数据结构（来自/user接口）
interface FileResponse {
  id: string; // MongoDB ObjectId
  username: string; // 后端返回username字段
  nickName: string; // 后端返回nickName字段
  source?: string;
  avatarPath?: string;
  createdAt?: string;
  updatedAt?: string;
  _class?: string;
  phone?: string;
  address?: string;
  department?: string;
  position?: string;
}

// 数据转换函数，将后端返回的数据转换为前端期望的格式
const convertToSecurityFileDetail = (
  responseData: SecureFileDetailResponse
): SecurityUserDetail => {
  return {
    username: responseData.username, // 后端实际返回username字段
    email: responseData.email,
    firstName: responseData.firstName,
    lastName: responseData.lastName,
    enabled: responseData.enabled,
    accountNonExpired: responseData.accountNonExpired,
    credentialsNonExpired: responseData.credentialsNonExpired,
    accountNonLocked: responseData.accountNonLocked,
    createdAt: responseData.createdAt,
    updatedAt: responseData.updatedAt,
    roles: responseData.roles,
  };
};

// 数据转换函数，将后端返回的文件数据转换为前端期望的格式
const convertToFile = (responseData: FileResponse): User => {
  return {
    username: responseData.username, // 前端User接口使用username字段作为主标识
    nickName: responseData.nickName, // 后端FileResponse使用nickName字段作为nickName
    email: "", // API返回的数据可能没有email字段，设置为空字符串
    firstName: "", // 可选字段，设置为空字符串
    lastName: "", // 可选字段，设置为空字符串
    avatarPath: responseData.avatarPath, // 直接使用avatarPath字段
    roles: [], // API返回的数据可能没有roles字段，设置为空数组
    createdAt: responseData.createdAt,
    updatedAt: responseData.updatedAt,
  };
};

// 类型守卫函数，确保数据是SecureFileDetailResponse类型
const isSecureFileDetailResponse = (
  data: any
): data is SecureFileDetailResponse => {
  // 更宽松的类型检查，允许部分字段为空或不存在
  if (!data || typeof data !== "object") {
    return false;
  }

  // 检查必需字段是否存在，但允许值为空
  // 后端实际返回username字段，但前端类型定义使用username字段
  const hasRequiredFields = "id" in data || "username" in data;

  // 检查字段类型，但允许值为null或undefined
  const idValid =
    !("id" in data) ||
    data.id === null ||
    data.id === undefined ||
    typeof data.id === "number";
  const usernameValid =
    !("username" in data) ||
    data.username === null ||
    data.username === undefined ||
    typeof data.username === "string";
  const emailValid =
    !("email" in data) ||
    data.email === null ||
    data.email === undefined ||
    typeof data.email === "string";

  return hasRequiredFields && idValid && usernameValid && emailValid;
};

// 类型守卫函数，确保数据是FileResponse类型
const isFileResponse = (data: any): data is FileResponse => {
  return (
    data &&
    typeof data === "object" &&
    "id" in data &&
    "username" in data &&
    "nickName" in data
  );
};

export const useFileSecureInfoQuery = () => {
  const setFileSecureInfo = useFileStore((state) => state.setFileSecureInfo);

  return useQuery<SecurityUserDetail | null>({
    queryKey: queryKeys.fileSecureInfo,
    queryFn: async () => {
      try {
        console.log("useFileSecureInfoQuery - 开始获取文件安全信息...");
        const response = await userAPI.getUserSecureInfo();
        console.log("useFileSecureInfoQuery - API响应:", response);

        // 检查API响应结构：可能是直接返回数据对象，也可能是ApiResponse结构
        if (response && isSecureFileDetailResponse(response)) {
          // API直接返回了文件安全信息数据对象
          console.log(
            "useFileSecureInfoQuery - API直接返回文件安全信息数据:",
            response
          );
          const securityFileDetail = convertToSecurityFileDetail(response);
          setFileSecureInfo(securityFileDetail.username, securityFileDetail);
          return securityFileDetail;
        } else if (
          response &&
          typeof response === "object" &&
          "data" in response
        ) {
          // API返回了ApiResponse结构
          if (response.data && isSecureFileDetailResponse(response.data)) {
            const securityFileDetail = convertToSecurityFileDetail(
              response.data
            );
            setFileSecureInfo(securityFileDetail.username, securityFileDetail);

            if (response.success) {
              console.log(
                "useFileSecureInfoQuery - API响应成功:",
                response.data
              );
            } else {
              console.warn(
                "useFileSecureInfoQuery - API返回了有效数据但success标志为false，仍然使用数据:",
                response.data
              );
            }
            return securityFileDetail;
          } else {
            // 没有有效数据时，根据success标志判断
            if (response.success) {
              console.warn(
                "useFileSecureInfoQuery - API响应success=true但没有有效数据，可能文件没有安全信息:",
                response
              );
            } else {
              console.warn(
                "useFileSecureInfoQuery - API响应失败，可能文件没有安全信息:",
                response
              );
            }
            return null;
          }
        } else if (response === null || response === undefined) {
          // 响应为空，可能是文件没有安全信息
          console.warn(
            "useFileSecureInfoQuery - API响应为空，可能文件没有安全信息"
          );
          return null;
        } else {
          // 未知的响应结构，尝试直接转换
          console.warn(
            "useFileSecureInfoQuery - API返回了未知的响应结构，尝试直接转换:",
            response
          );
          try {
            const securityFileDetail = convertToSecurityFileDetail(
              response as any
            );
            setFileSecureInfo(securityFileDetail.username, securityFileDetail);
            return securityFileDetail;
          } catch (error) {
            console.error("useFileSecureInfoQuery - 转换响应数据失败:", error);
            return null;
          }
        }
      } catch (error) {
        console.error("useFileSecureInfoQuery - API请求失败:", error);
        return null;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: 2, // 失败时重试2次
    retryDelay: 1000, // 重试间隔1秒
  });
};

// 文件相关变更hooks
export const useUpdateFileDetailMutation = () => {
  const queryClient = useQueryClient();
  const setFileDetail = useFileStore((state) => state.setFileDetail);
  const setCurrentFile = useFileStore((state) => state.setCurrentFile);

  return useMutation({
    mutationFn: (data: {
      username: string;
      nickName?: string;
      avatarPath?: string;
    }) => {
      console.log("调用updateFileDetail API，数据:", data);
      return userAPI.updateUserDetail(data);
    },
    onSuccess: (response, variables) => {
      console.log("updateFileDetail API响应:", response);

      if (response && response.success) {
        console.log("更新文件详情成功");

        // 如果API返回了更新后的数据，直接使用返回的数据更新本地状态
        if (response.data) {
          console.log("使用API返回的更新数据更新本地状态:", response.data);
          setFileDetail(response.data.username, response.data as UserDetail);

          // 同时更新currentFile状态，确保Header组件能获取到最新的头像路径
          // 检查response.data是否为FileResponse类型，如果是则转换为User类型
          if (isFileResponse(response.data)) {
            const currentFileData = convertToFile(response.data);
            setCurrentFile(currentFileData);
          } else {
            // 如果不是FileResponse类型，直接使用response.data作为User类型（包含avatarPath字段）
            setCurrentFile(response.data as User);
          }

          // 直接更新React Query缓存，避免重新获取数据
          // 更新currentFile缓存
          queryClient.setQueryData(queryKeys.currentFile, response.data);
          // 更新fileDetail缓存 - 这是information页面实际使用的缓存
          queryClient.setQueryData(
            queryKeys.fileDetail("current"),
            response.data
          );
        }

        // 只使fileSecureInfo查询失效，因为currentFile和fileDetail已经通过setQueryData更新了
        queryClient.invalidateQueries({ queryKey: queryKeys.fileSecureInfo });
      } else {
        console.error("更新文件详情失败，API响应success=false:", response);
      }
    },
    onError: (error) => {
      console.error("更新文件详情失败，网络或API错误:", error);
    },
  });
};

export const useUpdateFileSecureInfoMutation = () => {
  const queryClient = useQueryClient();
  const setFileSecureInfo = useFileStore((state) => state.setFileSecureInfo);

  const mutation = useMutation({
    mutationFn: (data: {
      email?: string;
      firstName?: string;
      lastName?: string;
    }) => {
      console.log("调用updateFileSecureInfo API，数据:", data);
      return userAPI.updateUserSecureInfo(data);
    },
    onSuccess: (response, variables) => {
      console.log("updateFileSecureInfo API响应:", response);

      if (response && response.success) {
        console.log("更新文件安全信息成功");

        // 如果API返回了更新后的数据，直接使用返回的数据更新本地状态
        if (response.data && isSecureFileDetailResponse(response.data)) {
          console.log("使用API返回的更新数据更新本地状态:", response.data);
          const securityFileDetail = convertToSecurityFileDetail(response.data);
          setFileSecureInfo(securityFileDetail.username, securityFileDetail);

          // 直接更新React Query缓存，避免重新获取数据
          queryClient.setQueryData(
            queryKeys.fileSecureInfo,
            securityFileDetail
          );
        }

        // 不需要使任何查询失效，因为fileSecureInfo已经通过setQueryData更新了
        // 移除queryClient.invalidateQueries调用，避免额外的GET请求
      } else {
        console.error("更新文件安全信息失败，API响应success=false:", response);
      }
    },
    onError: (error) => {
      console.error("更新文件安全信息失败，网络或API错误:", error);
    },
  });

  return {
    ...mutation,
    mutateAsync: mutation.mutateAsync,
  };
};

export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: (data: { password: string; confirmPassword: string }) =>
      userAPI.resetPassword(data),
    onSuccess: (response) => {
      if (response.success) {
        console.log("密码重置成功");
      }
    },
    onError: (error) => {
      console.error("密码重置失败:", error);
    },
  });
};

// 认证相关hooks（通过BFF AuthService处理）
export const useLoginMutation = () => {
  return useMutation({
    mutationFn: async (credentials: { username: string; password: string }) => {
      // BFF架构下，登录通过OAuth2流程处理
      // 这里只是触发登录跳转，实际的认证在BFF端完成
      console.log('触发BFF OAuth2登录流程...');
      const { bffAuthService } = await import('../services/BffAuthService');
      bffAuthService.login();
      return { success: true };
    },
    onSuccess: (response) => {
      if (response.success) {
        console.log("正在跳转到BFF登录页面...");
      }
    },
    onError: (error) => {
      console.error("登录失败:", error);
    },
  });
};

export const useLogoutMutation = () => {
  const queryClient = useQueryClient();
  const reset = useFileStore((state) => state.reset);

  return useMutation({
    mutationFn: async () => {
      // 通过BFF AuthService处理登出
      const { bffAuthService } = await import('../services/BffAuthService');
      await bffAuthService.logout();
      return { success: true };
    },
    onSuccess: () => {
      // 清除所有查询缓存
      queryClient.clear();
      // 重置文件状态
      reset();
    },
    onError: (error) => {
      console.error("登出失败:", error);
    },
  });
};

// 组合hooks - 提供更高级的抽象
export const useFileManagement = () => {
  const { data: currentFile, isLoading, error } = useFileQuery();
  const { data: fileSecureInfo } = useFileSecureInfoQuery();
  const updateFileMutation = useUpdateFileSecureInfoMutation();
  const resetPasswordMutation = useResetPasswordMutation();

  return {
    currentFile,
    fileSecureInfo,
    isLoading,
    error,
    updateFile: updateFileMutation.mutate,
    updateFileAsync: updateFileMutation.mutateAsync,
    resetPassword: resetPasswordMutation.mutate,
    resetPasswordAsync: resetPasswordMutation.mutateAsync,
  };
};
